import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Add, Close } from '@mui/icons-material'
import { Box, Button, Card, CardContent, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material'

import { PerformanceCondition, PerformanceConditionType, performanceConditionTypes, PerformanceOperator, performanceOperators } from '../../types/types'
import { getPerformanceValueError, performanceConditionTypeLabelKeys, performanceOperatorLabelKeys } from '../helpers/performanceHelpers'

type PerformanceConditionGroupProps = {
  conditions: PerformanceCondition[]
  label: string
  onChange: (conditionIndex: number, change: Partial<PerformanceCondition>) => void
  onRemove: (conditionIndex: number) => void
  onAdd: () => void
}

export const PerformanceConditionGroup: FC<PerformanceConditionGroupProps> = ({ conditions, label, onChange, onRemove, onAdd }) => {
  const { t } = useTranslation()

  return (
    <Card variant="outlined" aria-label={label}>
      <CardContent>
        <Stack spacing={1.5}>
          {conditions.map((condition, index) => {
            const error = getPerformanceValueError(condition.value)
            return (
              <Stack key={index} direction="row" spacing={1.5} alignItems="flex-start">
                {index > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ width: 40, pt: 1 }}>
                    {t('internal-service-management:settings_performance_and')}
                  </Typography>
                )}
                {index === 0 && <Box sx={{ width: 40 }} />}
                <TextField
                  select
                  size="small"
                  label={t('internal-service-management:settings_performance_type')}
                  value={condition.type}
                  onChange={(event) => onChange(index, { type: event.target.value as PerformanceConditionType })}
                  sx={{ width: 140 }}
                >
                  {performanceConditionTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(performanceConditionTypeLabelKeys[type])}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label={t('internal-service-management:settings_performance_operator')}
                  value={condition.operator}
                  onChange={(event) => onChange(index, { operator: event.target.value as PerformanceOperator })}
                  sx={{ width: 260 }}
                >
                  {performanceOperators.map((operator) => (
                    <MenuItem key={operator} value={operator}>
                      {t(performanceOperatorLabelKeys[operator])}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  type="number"
                  label={t('internal-service-management:settings_performance_value')}
                  value={condition.value}
                  onChange={(event) => onChange(index, { value: event.target.value })}
                  error={!!error}
                  helperText={error ? t(`internal-service-management:settings_performance_value_${error}`) : undefined}
                  sx={{ width: 140 }}
                />
                <Tooltip title={t('internal-service-management:settings_performance_remove_condition')}>
                  <IconButton aria-label={t('internal-service-management:settings_performance_remove_condition')} onClick={() => onRemove(index)}>
                    <Close />
                  </IconButton>
                </Tooltip>
              </Stack>
            )
          })}
          <Box>
            <Button size="small" startIcon={<Add />} onClick={onAdd}>
              {t('internal-service-management:settings_performance_add_and')}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}
